import { nodeSegment } from '../../shared/navigation.ts';
import { cableDropSchema } from '../../shared/schemas/cable-drop.ts';
import { clientSchema } from '../../shared/schemas/client.ts';
import { NODE_TYPES, pathSchema } from '../../shared/schemas/common.ts';
import { itemSchema } from '../../shared/schemas/item.ts';
import { propertySchema } from '../../shared/schemas/property.ts';
import { rackSchema } from '../../shared/schemas/rack.ts';
import {
  clientDetailSchema,
  propertyDetailSchema,
  roomDetailSchema,
  rackDetailSchema,
  itemDetailSchema,
  dropDetailSchema,
} from '../../shared/schemas/responses.ts';
import { roomSchema } from '../../shared/schemas/room.ts';
import { itemPaths, nestItemPaths } from '../../shared/tree.ts';
import { PARENT_PATH_OFFSET } from './constants.ts';
import { getEntity, OfflineParentError } from './store.ts';
import type { StoredEntity } from './db.ts';
import type { NodeType, PathEntry } from '../../shared/schemas/common.ts';
import type { RoomTree } from '../../shared/schemas/responses.ts';
import type { OfflineBundle } from '../../shared/schemas/sync.ts';

// One property's bundle, taken apart into the reads its pages ask for.
//
// A page does not know it is offline: it calls `useRoom(id)`, which reads
// `/api/rooms/<id>`, which `apiRequest` answers from the kept copy when the
// network is gone. So keeping a property means writing, under each of those
// paths, exactly the reply that path would have given — the same envelope,
// with the same keys — rather than a shape of our own that every page would
// then have to learn.
//
// What is deliberately absent: any credential or access-code value. The
// bundle carries which devices have credentials and when they changed
// (`sealed`), which is what the item page displays, and nothing more; the
// values leave the Worker only through the reveal route, which needs a
// connection and says so.

/** A read the copy answers, keyed by its path under /api. */
interface Read {
  readonly key: string;
  readonly body: unknown;
  readonly updatedAt: string | null;
}

function pathTo(
  base: readonly PathEntry[],
  type: NodeType,
  id: string,
  name: string,
  identifier: string,
): PathEntry[] {
  return [...base, { type, id, name, identifier }];
}

/** The rows of one list that belong to a node, e.g. the notes of a room. */
function forNode<T extends { readonly nodeType: string; readonly nodeId: string }>(
  rows: readonly T[],
  type: NodeType,
  id: string,
): T[] {
  return rows.filter((row) => row.nodeType === type && row.nodeId === id);
}

/** The per-node lists every node page reads: notes, photos, files, contacts. */
function attachedTo(
  bundle: Pick<OfflineBundle, 'notes' | 'photos' | 'attachments' | 'contacts'>,
  type: NodeType,
  id: string,
): Read[] {
  const segment = nodeSegment(type);
  return [
    {
      key: `${segment}/${id}/notes`,
      body: { notes: forNode(bundle.notes, type, id) },
      updatedAt: null,
    },
    {
      key: `${segment}/${id}/photos`,
      body: { photos: forNode(bundle.photos, type, id) },
      updatedAt: null,
    },
    {
      key: `${segment}/${id}/attachments`,
      body: { attachments: forNode(bundle.attachments, type, id) },
      updatedAt: null,
    },
    {
      key: `${segment}/${id}/contacts`,
      body: { contacts: forNode(bundle.contacts, type, id) },
      updatedAt: null,
    },
  ];
}

/** Whether a device has credentials stored, exactly as the item route states it. */
function credentialsOf(bundle: OfflineBundle, itemId: string): unknown {
  const sealed = bundle.sealed.find((row) => row.itemId === itemId);
  return sealed === undefined
    ? { stored: false, updatedAt: null }
    : { stored: true, updatedAt: sealed.updatedAt };
}

function itemReads(
  bundle: OfflineBundle,
  item: RoomTree['items'][number],
  paths: ReadonlyMap<string, PathEntry[]>,
): Read[] {
  const path = paths.get(item.id);
  if (path === undefined) {
    throw new Error('The offline device has no path');
  }
  return [
    {
      key: `items/${item.id}`,
      body: { item, path, credentials: credentialsOf(bundle, item.id) },
      updatedAt: item.updatedAt,
    },
    ...attachedTo(bundle, 'item', item.id),
  ];
}

function roomReads(
  bundle: OfflineBundle,
  room: RoomTree,
  base: PathEntry[],
  paths: ReadonlyMap<string, PathEntry[]>,
): Read[] {
  const path = pathTo(base, 'room', room.room.id, room.room.name, room.room.identifier);
  const reads: Read[] = [
    {
      key: `rooms/${room.room.id}`,
      body: {
        room: room.room,
        rooms: room.rooms.map((child) => child.room),
        racks: room.racks.map((entry) => entry.rack),
        items: room.items,
        drops: room.drops,
        path,
      },
      updatedAt: room.room.updatedAt,
    },
    ...attachedTo(bundle, 'room', room.room.id),
  ];
  for (const item of room.items) {
    reads.push(...itemReads(bundle, item, paths));
  }
  for (const entry of room.racks) {
    const rackPath = pathTo(path, 'rack', entry.rack.id, entry.rack.name, entry.rack.identifier);
    reads.push(
      {
        key: `racks/${entry.rack.id}`,
        body: { rack: entry.rack, items: entry.items, path: rackPath },
        updatedAt: entry.rack.updatedAt,
      },
      ...attachedTo(bundle, 'rack', entry.rack.id),
    );
    for (const item of entry.items) {
      reads.push(...itemReads(bundle, item, paths));
    }
  }
  for (const drop of room.drops) {
    reads.push(
      {
        key: `drops/${drop.id}`,
        body: {
          drop,
          fromNodeName: null,
          toNodeName: null,
          path: pathTo(path, 'drop', drop.id, drop.label, drop.identifier),
        },
        updatedAt: drop.updatedAt,
      },
      ...attachedTo(bundle, 'drop', drop.id),
    );
  }
  for (const child of room.rooms) {
    reads.push(...roomReads(bundle, child, path, paths));
  }
  return reads;
}

/**
 * Every read one property's bundle can answer, ready to be written into the
 * copy. `keptAt` is stamped on all of them together so the page can say when
 * the copy was taken.
 */
export function bundleReads(bundle: OfflineBundle, keptAt: string): StoredEntity[] {
  const { property, path } = bundle;
  const client = path.find((entry) => entry.type === 'client');
  if (client === undefined) {
    throw new Error('The offline property has no client path');
  }
  const paths = itemPaths({
    client: { id: client.id, name: client.name, identifier: client.identifier ?? '' },
    properties: [{ property, rooms: bundle.rooms }],
  });
  const reads: Read[] = [
    {
      key: `properties/${property.id}`,
      body: {
        property,
        rooms: bundle.rooms.map((room) => room.room),
        path,
        // Whether the property has access codes and when they last changed,
        // which is all the page shows. Revealing one needs a connection and
        // the reveal route says so.
        access: bundle.access,
      },
      updatedAt: property.updatedAt,
    },
    { key: `properties/${property.id}/vlans`, body: { vlans: bundle.vlans }, updatedAt: null },
    {
      key: `properties/${property.id}/checklists`,
      body: {
        checklists: bundle.checklists.map((entry) => ({
          ...entry.checklist,
          done: entry.items.filter((step) => step.done).length,
          total: entry.items.length,
        })),
      },
      updatedAt: null,
    },
    {
      key: `properties/${property.id}/inventory`,
      body: { items: bundle.rooms.flatMap((room) => itemsIn(room)) },
      updatedAt: null,
    },
    ...(bundle.records === undefined
      ? []
      : [
          {
            key: `properties/${property.id}/records`,
            body: { records: bundle.records },
            updatedAt: null,
          },
        ]),
    ...(bundle.customTypes === undefined
      ? []
      : [{ key: 'custom-types', body: { types: bundle.customTypes }, updatedAt: null }]),
    ...attachedTo(bundle, 'property', property.id),
  ];
  for (const record of bundle.records ?? [])
    reads.push({ key: `records/${record.id}`, body: { record }, updatedAt: record.updatedAt });
  for (const entry of bundle.checklists) {
    reads.push({
      key: `checklists/${entry.checklist.id}`,
      body: { checklist: entry.checklist, items: entry.items, path },
      updatedAt: entry.checklist.updatedAt,
    });
  }
  for (const room of bundle.rooms) {
    reads.push(...roomReads(bundle, room, path, paths));
  }
  return reads.map((read) => ({ ...read, keptAt }));
}

/** Every device under a room, however deep it nests. */
function itemsIn(room: RoomTree): RoomTree['items'][number][] {
  return [
    ...room.items,
    ...room.racks.flatMap((entry) => entry.items),
    ...room.rooms.flatMap((child) => itemsIn(child)),
  ];
}

const NODE_SCHEMAS = {
  client: clientSchema,
  property: propertySchema,
  room: roomSchema,
  rack: rackSchema,
  item: itemSchema,
  drop: cableDropSchema,
};
const DETAIL_SCHEMAS = {
  client: clientDetailSchema,
  property: propertyDetailSchema,
  room: roomDetailSchema,
  rack: rackDetailSchema,
  item: itemDetailSchema,
  drop: dropDetailSchema,
};

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Load and bind the cached path to its actual node, without trusting a caller-supplied trail. */
async function cachedParent(key: string) {
  const type = NODE_TYPES.find((candidate) => key.startsWith(`${nodeSegment(candidate)}/`));
  const stored = await getEntity(key);
  if (type === undefined || stored === null || !record(stored.body)) {
    throw new OfflineParentError('Keep the parent on this device before adding a node offline.');
  }
  const node = NODE_SCHEMAS[type].safeParse(stored.body[type]);
  const path = pathSchema.safeParse(stored.body['path']);
  if (!node.success || !path.success || path.data[0]?.type !== 'client') {
    throw new OfflineParentError(
      'The cached parent is incomplete. Reconnect and refresh it before adding a node.',
    );
  }
  if (
    (type === 'client' && path.data.length !== 1) ||
    (type === 'property' && path.data.length !== 2)
  ) {
    throw new OfflineParentError(
      'The cached parent ancestry is inconsistent. Reconnect and refresh it.',
    );
  }
  const last = path.data.at(-1);
  if (
    last?.type !== type ||
    last.id !== node.data.id ||
    key !== `${nodeSegment(type)}/${node.data.id}`
  ) {
    throw new OfflineParentError(
      'The cached parent path belongs to a different node. Reconnect and refresh it.',
    );
  }
  return { type, node: node.data, path: path.data, body: stored.body };
}

/** Confirm that the cached parent's property and client match the same authoritative cached property. */
async function parentProperty(parent: Awaited<ReturnType<typeof cachedParent>>): Promise<string> {
  const propertyId = parent.path.find((entry) => entry.type === 'property')?.id;
  if (propertyId === undefined) {
    throw new OfflineParentError(
      'The cached parent has no property path. Reconnect and refresh it.',
    );
  }
  const saved =
    parent.type === 'property' ? parent : await cachedParent(`properties/${propertyId}`);
  if (
    !('clientId' in saved.node) ||
    saved.node.id !== propertyId ||
    saved.node.clientId !== parent.path[0]?.id ||
    ('propertyId' in parent.node && parent.node.propertyId !== propertyId)
  ) {
    throw new OfflineParentError(
      'The cached parent belongs to a different property or client. Reconnect and refresh it.',
    );
  }
  const above = parent.path.at(PARENT_PATH_OFFSET);
  if (parent.type === 'rack') {
    const rack = rackSchema.parse(parent.node);
    if (above?.type !== 'room' || above.id !== rack.roomId) {
      throw new OfflineParentError(
        'The cached parent belongs to a different room. Reconnect and refresh it.',
      );
    }
  }
  if (parent.type === 'room') {
    const room = roomSchema.parse(parent.node);
    if (
      above?.id !== (room.parentRoomId ?? room.propertyId) ||
      above.type !== (room.parentRoomId === null ? 'property' : 'room')
    ) {
      throw new OfflineParentError(
        'The cached room ancestry is inconsistent. Reconnect and refresh it.',
      );
    }
  }
  return propertyId;
}

/** Build the actual empty-child detail of a newly queued node before anything enters the queue. */
export async function createdNodeDetail(
  type: NodeType,
  input: Record<string, unknown>,
  parentKey: string | undefined,
): Promise<{
  readonly row: Record<string, unknown>;
  readonly body: Record<string, unknown>;
  readonly related: readonly Read[];
}> {
  let row = { ...input };
  let base: PathEntry[] = [];
  let parent: Awaited<ReturnType<typeof cachedParent>> | undefined;
  if (type !== 'client') {
    if (parentKey === undefined) {
      throw new OfflineParentError('A new offline node needs its cached parent.');
    }
    parent = await cachedParent(parentKey);
    base = parent.path;
    const id = parent.node.id;
    const propertyId = type === 'property' ? null : await parentProperty(parent);
    const fits =
      type === 'property'
        ? parent.type === 'client' && row['clientId'] === id
        : type === 'room'
          ? (parent.type === 'property' &&
              row['parentRoomId'] === null &&
              row['propertyId'] === id) ||
            (parent.type === 'room' &&
              row['parentRoomId'] === id &&
              (row['propertyId'] === '' || row['propertyId'] === propertyId))
          : type === 'rack' || type === 'drop'
            ? parent.type === 'room' && row['roomId'] === id
            : (parent.type === 'room' && row['roomId'] === id && row['rackId'] === null) ||
              (parent.type === 'rack' && row['rackId'] === id && row['roomId'] === null) ||
              (parent.type === 'property' && row['roomId'] === null && row['rackId'] === null);
    if (!fits) {
      throw new OfflineParentError(
        'The new node does not belong to this cached parent. Nothing was queued.',
      );
    }
    if (type === 'room') {
      row = { ...row, propertyId };
    }
  }
  const node = NODE_SCHEMAS[type].parse(row);
  const name = 'name' in node ? node.name : node.label;
  let path = pathTo(base, type, node.id, name, node.identifier);
  if (type === 'item') {
    const item = itemSchema.parse(node);
    const paths = new Map<string, PathEntry[]>();
    const items = new Map<string, typeof item>();
    const siblings = parent?.body['items'];
    if (Array.isArray(siblings)) {
      for (const candidate of siblings) {
        const sibling = itemSchema.parse(candidate);
        items.set(sibling.id, sibling);
        paths.set(sibling.id, pathTo(base, 'item', sibling.id, sibling.name, sibling.identifier));
      }
    }
    const ancestorId = item.portParentItemId ?? item.parentItemId;
    if (ancestorId !== null && !items.has(ancestorId)) {
      const ancestor = await cachedParent(`items/${ancestorId}`);
      if (
        ancestor.path[0]?.id !== base[0]?.id ||
        ancestor.path.find((entry) => entry.type === 'property')?.id !==
          base.find((entry) => entry.type === 'property')?.id
      ) {
        throw new OfflineParentError(
          'The device ancestor belongs to a different property or client. Nothing was queued.',
        );
      }
      paths.set(ancestorId, ancestor.path);
    }
    items.set(item.id, item);
    paths.set(item.id, path);
    const nested = nestItemPaths(paths, items).get(item.id);
    if (nested === undefined) {
      throw new OfflineParentError('The new device has no cached path. Nothing was queued.');
    }
    path = nested;
  }
  const body = DETAIL_SCHEMAS[type].parse({
    [type]: node,
    path,
    properties: [],
    rooms: [],
    racks: [],
    items: [],
    drops: [],
    fromNodeName: null,
    toNodeName: null,
  });
  return {
    row: node,
    body,
    related: attachedTo({ notes: [], photos: [], attachments: [], contacts: [] }, type, node.id),
  };
}
